import type { FormDescriptor, FormFieldDescriptor, FormFieldId, PayloadBuilderId } from '@finance/contracts';
import { applyOutputTransform } from './value-transformer';
import { getPayloadBuilder, type PayloadBuilder } from './payload-builder-registry';
import { getPayloadSchema } from './payload-schema-registry';
import type { ResolvedFieldState } from './field-state';
export type { PayloadBuilder } from './payload-builder-registry';

export function getFieldNameById(
  descriptor: FormDescriptor,
  fieldId: FormFieldId,
): string | undefined {
  return descriptor.fields.find((f) => (f.id as string) === (fieldId as string))?.name;
}

export function applyFieldSelection(
  values: Record<string, unknown>,
  descriptor: FormDescriptor,
  includeFields?: FormFieldId[],
  excludeFields?: FormFieldId[],
): Record<string, unknown> {
  const result = { ...values };

  const includeNames = new Set<string>();
  if (includeFields && includeFields.length > 0) {
    for (const id of includeFields) {
      const name = getFieldNameById(descriptor, id);
      if (!name) {
        throw new Error(`Unresolved include field ID: ${String(id)} — field does not exist in descriptor ${String(descriptor.id)}`);
      }
      includeNames.add(name);
    }
    for (const key of Object.keys(result)) {
      if (!includeNames.has(key)) {
        delete result[key];
      }
    }
  }

  if (excludeFields && excludeFields.length > 0) {
    for (const id of excludeFields) {
      const name = getFieldNameById(descriptor, id);
      if (!name) {
        throw new Error(`Unresolved exclude field ID: ${String(id)} — field does not exist in descriptor ${String(descriptor.id)}`);
      }
      if (name in result) {
        delete result[name];
      }
    }
  }

  return result;
}

export function applySensitivePolicies(
  values: Record<string, unknown>,
  fields: FormFieldDescriptor[],
): Record<string, unknown> {
  const result = { ...values };
  for (const field of fields) {
    if (field.sensitive) {
      const behavior = field.sensitive.payloadBehavior;
      if (behavior === 'omit' || behavior === 'hash' || behavior === 'encrypt') {
        delete result[field.name];
      }
    }
  }
  return result;
}

/**
 * Determine which field names are eligible for inclusion in the submission payload.
 *
 * A field is eligible when:
 * - It is visible (resolved field state).
 * - It is not access-denied.
 * - It is not masked with a behavior that prevents submission.
 *
 * This replaces the previous `visibleFieldNames` approach which only checked
 * visibility without considering access-denied or masked fields.
 */
export function getEligibleFieldNames(
  descriptor: FormDescriptor,
  fieldStates: Map<string, ResolvedFieldState>,
): Set<string> {
  const eligible = new Set<string>();
  for (const field of descriptor.fields) {
    const state = fieldStates.get(field.name);
    if (!state) continue;
    if (!isFieldEligibleForSubmission(field, state)) continue;
    eligible.add(field.name);
  }
  return eligible;
}

export function isFieldEligibleForSubmission(
  field: FormFieldDescriptor,
  state: ResolvedFieldState,
): boolean {
  if (state.accessDenied) return false;
  if (state.masked) return false;

  const submitBehavior = field.value?.submitBehavior ?? 'include';
  if (submitBehavior === 'exclude' || submitBehavior === 'server-owned') return false;

  if (field.value?.computedValueExpression && field.value?.outputOnly) return false;

  if (!state.visible) {
    const clearWhenHidden = field.value?.clearWhenHidden ?? field.value?.hiddenValuePolicy === 'clear';
    const retainWhenHidden = field.value?.retainWhenHidden ?? field.value?.hiddenValuePolicy === 'preserve';
    if (clearWhenHidden || !retainWhenHidden) return false;
  }

  if (state.readonly && field.display?.readonlySubmission !== 'original') return false;

  return true;
}

export interface BuildFormPayloadOptions {
  includeFields?: FormFieldId[];
  excludeFields?: FormFieldId[];
  payloadBuilderId?: PayloadBuilderId;
  /** Resolved field states for full eligibility checks (visibility + access). */
  fieldStates: Map<string, ResolvedFieldState>;
  /** Command-scoped schema reference used to validate payload-builder output. */
  payloadSchemaRef?: string;
}

function getProhibitedSourceFieldNames(
  descriptor: FormDescriptor,
  fieldStates: Map<string, ResolvedFieldState>,
): Set<string> {
  const prohibited = new Set<string>();
  for (const field of descriptor.fields) {
    const state = fieldStates.get(field.name);
    if (!state || !isFieldEligibleForSubmission(field, state)) {
      prohibited.add(field.name);
      continue;
    }
    if (field.sensitive) {
      const behavior = field.sensitive.payloadBehavior;
      if (behavior === 'omit' || behavior === 'hash' || behavior === 'encrypt') {
        prohibited.add(field.name);
      }
    }
    const submitBehavior = field.value?.submitBehavior ?? 'include';
    if (submitBehavior === 'exclude' || submitBehavior === 'server-owned') {
      prohibited.add(field.name);
    }
  }
  return prohibited;
}

function validatePayloadSchema(
  payload: Record<string, unknown>,
  schemaRef: string,
): { allowedKeys: Set<string>; requiredKeys: readonly string[] } {
  const schema = getPayloadSchema(schemaRef);
  if (!schema) {
    throw new Error(`Unknown payload schema: ${schemaRef}`);
  }

  if (schema.parser) {
    try {
      const parsed = schema.parser(payload);
      for (const key of Object.keys(payload)) {
        delete payload[key];
      }
      Object.assign(payload, parsed);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Payload failed schema parser validation for ${schemaRef}: ${message}`);
    }
  }

  const allowed = new Set(schema.allowedKeys);
  for (const key of Object.keys(payload)) {
    if (!allowed.has(key)) {
      throw new Error(`Payload key "${key}" is not allowed by schema ${schemaRef}`);
    }
  }

  const requiredKeys = schema.requiredKeys ?? [];
  for (const key of requiredKeys) {
    if (!(key in payload)) {
      throw new Error(`Missing required payload key "${key}" for schema ${schemaRef}`);
    }
    const value = payload[key];
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim().length === 0)
    ) {
      throw new Error(`Required payload key "${key}" has an empty value for schema ${schemaRef}`);
    }
  }

  return { allowedKeys: allowed, requiredKeys };
}

export function buildFormPayload(
  values: Record<string, unknown>,
  descriptor: FormDescriptor,
  options: BuildFormPayloadOptions,
): Record<string, unknown> {
  let payload = { ...values };

  const eligibleNames = getEligibleFieldNames(descriptor, options.fieldStates);
  const prohibitedSourceFields = getProhibitedSourceFieldNames(descriptor, options.fieldStates);

  for (const key of Object.keys(payload)) {
    if (!eligibleNames.has(key)) {
      delete payload[key];
    }
  }

  for (const field of descriptor.fields) {
    if (field.value?.outputTransformId && payload[field.name] !== undefined) {
      payload[field.name] = applyOutputTransform(payload[field.name], field.value.outputTransformId);
    }
  }

  payload = applyFieldSelection(payload, descriptor, options.includeFields, options.excludeFields);
  payload = applySensitivePolicies(payload, descriptor.fields);

  const sourceOnlyPayload = { ...payload };
  let provenance: Record<string, string[]> = Object.fromEntries(
    Object.keys(payload).map((key) => [key, [key]]),
  );
  let payloadBuilderUsed = false;
  let schemaAllowedKeys: Set<string> | undefined;

  if (options.payloadBuilderId) {
    payloadBuilderUsed = true;
    if (!options.payloadSchemaRef) {
      throw new Error('payloadSchemaRef is required when payloadBuilderId is used');
    }

    const builder = getPayloadBuilder(options.payloadBuilderId);
    if (!builder) {
      throw new Error(`Unknown payload builder: ${String(options.payloadBuilderId)}`);
    }

    const result = builder.buildWithProvenance
      ? builder.buildWithProvenance(payload, descriptor)
      : { payload: builder.build ? builder.build(payload, descriptor) : payload, provenance: undefined };

    payload = result.payload;
    provenance = result.provenance ?? provenance;

    if (builder.fieldBindings) {
      for (const [outputKey, binding] of Object.entries(builder.fieldBindings)) {
        provenance[outputKey] = [...binding.sourceFields];
      }
    }

    const schemaValidationResult = validatePayloadSchema(payload, options.payloadSchemaRef);
    schemaAllowedKeys = schemaValidationResult.allowedKeys;

    for (const outputKey of Object.keys(payload)) {
      const sources = provenance[outputKey];
      if (!sources) {
        throw new Error(
          `Payload output "${outputKey}" is missing provenance. Builders that rename/derive fields must provide provenance via buildWithProvenance or fieldBindings.`,
        );
      }
    }

    for (const [outputKey, sourceFields] of Object.entries(provenance)) {
      if (!(outputKey in payload)) {
        continue;
      }
      for (const sourceField of sourceFields) {
        if (prohibitedSourceFields.has(sourceField)) {
          throw new Error(
            `Payload output "${outputKey}" derives from prohibited source field "${sourceField}"`,
          );
        }
      }
    }
  }

  if (!payloadBuilderUsed) {
    for (const key of Object.keys(payload)) {
      if (!eligibleNames.has(key) || prohibitedSourceFields.has(key)) {
        delete payload[key];
      }
    }
    return payload;
  }

  const scrubbedPayload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (schemaAllowedKeys && !schemaAllowedKeys.has(key)) {
      continue;
    }

    const sourceFields = provenance[key] ?? [];
    if (sourceFields.some((sourceField) => prohibitedSourceFields.has(sourceField))) {
      continue;
    }

    const isSourceKey = key in sourceOnlyPayload;
    if (isSourceKey && !eligibleNames.has(key)) {
      continue;
    }

    scrubbedPayload[key] = value;
  }

  return scrubbedPayload;
}
